import { Box } from "@mui/material";
import VerificationLayout from "../../../../layout/VerificationLayout.component";
import { ArtifactStyle } from "./VerficationArtifactsStyles";
import { CheckCircle } from "@mui/icons-material";
import Theme from "../../../../../utils/Theme";
import { useRef, useState, useEffect } from "react";
import AppButton from "../../../../base/AppButton.component";
import ArtifactButton from "../../../../base/dialog-body/ArtifactButton.component";
import { useMutation, useQuery } from "@apollo/client";
import { CSI_ALERT, SUBMIT_CSI } from "../../../../../graphql/mutation/mutation";
import { useParams, useNavigate } from "react-router-dom";

import ContentShimmer from "react-content-shimmer";
import { FETCH_CSI_DETAILS } from "../../../../../graphql/query/query";

const SubmitCsi = () => {
  const params = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [success, setSuccess] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSpanish, setIsSpanish] = useState(false);
  const [isSpokeperson, setIsSpokeperson] = useState(true);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);

  // Video states
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [duration, setDuration] = useState(0);
  const [showButtons, setShowButtons] = useState(false);

  const { data } = useQuery(FETCH_CSI_DETAILS, {
    variables: {
      userId: parseInt(params?.userId),
      employeeUserId: parseInt(params?.employeeId),
      dealershipId: parseInt(params?.dealershipId),
    },
  });

  const [csiAlert, { loading: csiLoading }] = useMutation(CSI_ALERT);
  const [submitCsi, { loading: submitCsiLoading }] = useMutation(SUBMIT_CSI);

  // Set language based on user_language_id from API
  useEffect(() => {
    const userLanguageId = data?.fetchCsiDetails?.user_language_id;
    if (userLanguageId === 2) {
      setIsSpanish(true);
    } else if (userLanguageId === 1) {
      setIsSpanish(false);
    }
  }, [data]);

  // Derive video URL
  useEffect(() => {
    if (!isSpokeperson) {
      setSelectedUrl(null);
      setShowButtons(false);
      return;
    }
    const url = data?.fetchCsiDetails?.second_review_ai_url?.selected_url;
    setSelectedUrl(url || null);
    setShowButtons(false);
    setAnimateIn(false);
  }, [isSpokeperson, data]);

  // Video lifecycle
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selectedUrl) return;
    const handleLoadedMetadata = () => setDuration(video.duration);
    const handleCanPlay = () => {
      setAnimateIn(true);
      video.play().catch(() => {});
    };
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("canplay", handleCanPlay);
    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [selectedUrl]);

  // Show YES/NO near end of video
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleTimeUpdate = () => {
      const remaining = (video.duration || 0) - (video.currentTime || 0);
      setShowButtons(remaining <= 1);
    };
    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [duration]);

  // Pause/reset when spokesperson turned off
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!isSpokeperson) {
      try {
        video.pause();
        video.currentTime = 0;
        video.removeAttribute("src");
        video.load();
      } catch (_) {}
      setAnimateIn(false);
      setShowButtons(false);
    }
  }, [isSpokeperson]);

  const handleYesNoClick = (answer) => {
    setSelectedAnswer(answer);
    setSubmitted(false);
  };

  // ── CLOSE handler - go back
  const handleClose = () => {
    window.history.back();
  };

  const submitHandler = () => {
    if (!selectedAnswer) return;

    if (selectedAnswer === "yes") {
      submitCsi({
        variables: {
          csiInput: {
            user_id: parseInt(params?.userId),
            dealership_id: parseInt(params?.dealershipId),
            employee_user_id: parseInt(params?.employeeId),
            second_review_answer: "yes",
          },
        },
      })
        .then((res) => {
          if (res?.data?.submitCsiLink?.status) {
            setSuccess(true);
            setSubmitted(true);
            setTimeout(() => {
              setSuccess(false);
              setSubmitted(false);
            }, 3000);
          }
        })
        .catch((err) => {
          console.log("CSI YES error", err);
        });
    } else if (selectedAnswer === "no") {
      submitCsi({
        variables: {
          csiInput: {
            user_id: parseInt(params?.userId),
            dealership_id: parseInt(params?.dealershipId),
            employee_user_id: parseInt(params?.employeeId),
            second_review_answer: "no",
          },
        },
      })
        .then(() => {
          return csiAlert({
            variables: {
              alertInput: {
                user_id: parseInt(params?.userId),
              },
            },
          });
        })
        .then((res) => {
          if (res?.data?.csiAlert?.status) {
            setSuccess(true);
            setSubmitted(true);
            setTimeout(() => {
              setSuccess(false);
              setSubmitted(false);
            }, 3000);
          }
        })
        .catch((err) => {
          console.log("CSI NO error", err);
        });
    }
  };

  const isLoading = csiLoading || submitCsiLoading;

  // ── TOGGLE (dropdowns only - left section)
  const Toggle = () => {
    const dropdownButtonStyles = {
      padding: "13px 7px",
      fontSize: window.innerWidth <= 750 ? "10px" : "12px",
      cursor: "pointer",
      color: Theme.palette.primary.main,
      fontWeight: 500,
      width: window.innerWidth <= 750 ? "70px" : "80px",
      height: window.innerWidth <= 750 ? "32px" : "36px",
      display: "flex",
      alignItems: "center",
      position: "relative",
      justifyContent: "space-between",
    };

    const dropdownContentStyles = {
      position: "absolute",
      top: "100%",
      left: "0%",
      backgroundColor: "#fff",
      width: {
        xs: "100%",
        sm: "70%",
        md: "60%",
        lg: "65%",
      },
      boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
      zIndex: 1000,
      borderRadius: "4px",
      border: "1px solid #ddd",
      marginTop: "4px",
      overflow: "hidden",
    };

    const dropdownItemStyles = {
      padding: "10px 12px",
      cursor: "pointer",
      fontSize: window.innerWidth <= 750 ? "10px" : "12px",
      backgroundColor: "#fff",
      "&:hover": { backgroundColor: "#f5f5f5" },
    };

    const selectedItemStyles = {
      ...dropdownItemStyles,
      backgroundColor: "#defae5ff",
      fontWeight: 600,
      color: Theme.palette.primary.main,
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", position: "relative" }}>
        {/* Language Dropdown */}
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={() => { setLanguageOpen(!languageOpen); setModeOpen(false); }}
            sx={dropdownButtonStyles}
          >
            <span style={{ flex: 1, textAlign: "start" }}>
              {isSpanish ? "SPANISH" : "ENGLISH"}
            </span>
            <span style={{ fontSize: "12px", color: Theme.palette.primary.main }}>▼</span>
          </Box>
          {languageOpen && (
            <Box sx={dropdownContentStyles}>
              <Box onClick={() => { setIsSpanish(false); setLanguageOpen(false); }} sx={!isSpanish ? selectedItemStyles : dropdownItemStyles}>
                ENGLISH
              </Box>
              <Box onClick={() => { setIsSpanish(true); setLanguageOpen(false); }} sx={isSpanish ? selectedItemStyles : dropdownItemStyles}>
                SPANISH
              </Box>
            </Box>
          )}
        </Box>

        {/* Mode Dropdown */}
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={() => { setModeOpen(!modeOpen); setLanguageOpen(false); }}
            sx={{
              ...dropdownButtonStyles,
              opacity: data?.fetchCsiDetails?.second_review_ai_url ? 1 : 0.5,
              pointerEvents: data?.fetchCsiDetails?.second_review_ai_url ? "auto" : "none",
            }}
          >
            <span style={{ flex: 1, textAlign: "start" }}>
              {isSpokeperson ? "AVATAR" : "READ"}
            </span>
            <span style={{ fontSize: "12px", color: Theme.palette.primary.main }}>▼</span>
          </Box>
          {modeOpen && (
            <Box sx={dropdownContentStyles}>
              <Box onClick={() => { setIsSpokeperson(true); setModeOpen(false); }} sx={isSpokeperson ? selectedItemStyles : dropdownItemStyles}>
                AVATAR
              </Box>
              <Box onClick={() => { setIsSpokeperson(false); setModeOpen(false); }} sx={!isSpokeperson ? selectedItemStyles : dropdownItemStyles}>
                READ
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const getQuestionText = () => {
    if (isSpanish) {
      return (
        data?.fetchCsiDetails?.es_second_review_question ||
        data?.fetchCsiDetails?.second_review_question ||
        "¡Le agradecemos que nos haya elegido! Nuestro equipo se esfuerza por brindarle una experiencia de 5 estrellas. ¿Diría usted que nos hemos ganado esa calificación?"
      );
    }
    return (
      data?.fetchCsiDetails?.second_review_question ||
      "We appreciate you choosing us! Our team strives for a 5-star experience. Would you say we earned that rating?"
    );
  };

  const getYesText = () => (isSpanish ? "Sí" : "Yes");
  const getNoText = () => (isSpanish ? "No" : "No");

  // Submit button component (shared)
  const SubmitButton = () => (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#fff",
        py: 2,
        zIndex: 10,
      }}
    >
      {success ? (
        <Box sx={{ display: "flex", my: "14px" }}>
          <CheckCircle color={"primary"} />
          <span style={{ color: Theme.palette.primary.main, marginLeft: "4px" }}>
            {isSpanish ? "ÉXITO" : "SUCCESS"}
          </span>
        </Box>
      ) : (
        <AppButton
          buttonText={isSpanish ? "Entregar" : "Submit"}
          disabled={!selectedAnswer || isLoading}
          clickHandler={submitHandler}
          sx={{ width: "80%", alignSelf: "center" }}
          loading={isLoading}
        />
      )}
    </Box>
  );

  return (
    <VerificationLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "10px",
          height: "100%",
          position: "relative",
        }}
      >
        {/* ── HEADER ROW: [Toggle | Logo | Close] - matches ToggleComponent layout ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          {/* LEFT: Language + Mode dropdowns */}
          <Box sx={{ width: "30%" }}>
            <Toggle />
          </Box>

          {/* CENTER: Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {data?.fetchCsiDetails && (
              <img
                src={data?.fetchCsiDetails.dealership_logo}
                width={150}
                height={60}
                alt="dealership logo"
              />
            )}
          </Box>

          {/* RIGHT: Close button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              width: "30%",
            }}
          >
            <AppButton
              buttonText={"close"}
              variant={"text"}
              sx={{
                color: Theme.palette.error.main,
                fontSize: window.innerWidth <= 750 ? "12px" : "16px",
              }}
              clickHandler={handleClose}
            />
          </Box>
        </Box>

        {/* ── BODY ── */}
        {isSpokeperson && selectedUrl ? (
          // ── AVATAR MODE ──
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
            }}
          >
            <video
              ref={videoRef}
              key={selectedUrl}
              controls
              preload="metadata"
              style={{ display: "none" }}
            />

            <Box
              component="video"
              ref={videoRef}
              key={selectedUrl}
              controls
              preload="metadata"
              sx={{
                height: { xs: "74vh", sm: "100vh", md: "80vh" },
                maxHeight: { xs: "520px", sm: "750px", md: "497px" },
                width: { xs: "90%", sm: "90%", md: "50%" },
                maxWidth: { xs: "280px", sm: "400px", md: "250px" },
                objectFit: "cover",
                objectPosition: "center",
                transition: "opacity 0.4s ease-in-out",
                opacity: animateIn ? 1 : 0,
                backgroundColor: "#fff",
                borderRadius: "12px",
              }}
            >
              <source src={selectedUrl} type="video/mp4" />
            </Box>

            {/* YES/NO overlay at end of video */}
            {showButtons && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 130,
                  zIndex: 3,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  p: "5px",
                  borderRadius: "5px",
                  transition: "opacity 0.6s ease, transform 0.6s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ArtifactButton
                    text={getYesText()}
                    variant={selectedAnswer === "yes" ? "contained" : "outlined"}
                    onClick={() => handleYesNoClick("yes")}
                    sx={{ bgcolor: selectedAnswer === "yes" ? null : "white", minWidth: "100px" }}
                  />
                  <ArtifactButton
                    text={getNoText()}
                    variant={selectedAnswer === "no" ? "contained" : "outlined"}
                    onClick={() => handleYesNoClick("no")}
                    sx={{ bgcolor: selectedAnswer === "no" ? null : "white", minWidth: "100px" }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          // ── READ MODE ──
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: "8px",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            {data?.fetchCsiDetails ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  mt: 10,
                  gap: 3,
                }}
              >
                <pre style={ArtifactStyle.textBox}>{getQuestionText()}</pre>

                {/* YES / NO Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ArtifactButton
                    text={getYesText()}
                    variant={selectedAnswer === "yes" ? "contained" : "outlined"}
                    onClick={() => handleYesNoClick("yes")}
                    sx={{ bgcolor: selectedAnswer === "yes" ? null : "white", minWidth: "100px" }}
                  />
                  <ArtifactButton
                    text={getNoText()}
                    variant={selectedAnswer === "no" ? "contained" : "outlined"}
                    onClick={() => handleYesNoClick("no")}
                    sx={{ bgcolor: selectedAnswer === "no" ? null : "white", minWidth: "100px" }}
                  />
                </Box>
              </Box>
            ) : (
              <Box>
                <ContentShimmer />
              </Box>
            )}
          </Box>
        )}

        {/* Single Submit Button - Same for both modes */}
        <SubmitButton />
      </Box>
    </VerificationLayout>
  );
};

export default SubmitCsi;